import React, { useEffect, useState, useRef, useContext, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  makeStyles,
  CircularProgress,
  TextField,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import FlashOn from "@material-ui/icons/FlashOn";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import QuickMessageModal from "../QuickMessageModal";
import { i18n } from "../../translate/i18n";
import { format, parseISO } from "date-fns";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";

const INITIAL_DAYS_WINDOW = 3;

const useStyles = makeStyles((theme) => ({
  content: {
    padding: 0,
    minHeight: 320,
    maxHeight: "70vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#e5ddd5",
    backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"100\" height=\"100\" viewBox=\"0 0 100 100\"><rect fill=\"%23e5ddd5\" width=\"100\" height=\"100\"/><path fill=\"%23d1cbc4\" d=\"M0 50 L50 0 L100 50 L50 100 Z\" opacity=\".05\"/></svg>')",
  },
  messagesWrap: {
    flex: 1,
    overflowY: "auto",
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    ...theme.scrollbarStyles,
  },
  bubble: {
    maxWidth: "85%",
    padding: "8px 12px",
    borderRadius: 8,
    wordBreak: "break-word",
    boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
    alignSelf: "flex-end",
  },
  bubbleFromMe: {
    backgroundColor: "#dcf8c6",
    marginLeft: "auto",
    borderTopRightRadius: 2,
  },
  bubbleFromContact: {
    backgroundColor: "#fff",
    alignSelf: "flex-start",
    marginRight: "auto",
    borderTopLeftRadius: 2,
  },
  bubbleDate: {
    fontSize: 11,
    color: "rgba(0,0,0,0.45)",
    marginTop: 2,
  },
  bubbleBody: {
    fontSize: 14,
    whiteSpace: "pre-wrap",
  },
  bubbleMedia: {
    maxWidth: 260,
    maxHeight: 260,
    borderRadius: 6,
    marginTop: 4,
    display: "block",
  },
  loadingWrap: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  emptyWrap: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    color: "rgba(0,0,0,0.5)",
  },
  composer: {
    borderTop: "1px solid rgba(0,0,0,0.08)",
    padding: theme.spacing(1, 1.5),
    backgroundColor: "#f5f5f5",
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  composerInput: {
    flex: 1,
    minWidth: 0,
  },
  composerHint: {
    padding: theme.spacing(0.5, 1.5, 1),
    backgroundColor: "#f5f5f5",
    borderTop: "1px solid rgba(0,0,0,0.08)",
  },
  statusBannerPending: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 152, 0, 0.14)"
        : "#fff3e0",
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    "& svg": {
      marginTop: 2,
      color: theme.palette.warning.main,
      flexShrink: 0,
    },
  },
  statusBannerClosed: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(33, 150, 243, 0.12)"
        : "#e3f2fd",
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    "& svg": {
      marginTop: 2,
      color: theme.palette.info.main,
      flexShrink: 0,
    },
  },
}));

function isImageMessage(msg) {
  const t = (msg.mediaType || "").toLowerCase();
  return t === "image" || t === "imagem" || t.includes("image");
}

function getMediaUrl(msg) {
  const url = msg.mediaUrl || (msg.media && msg.media.url);
  if (!url) return "";
  if (url.startsWith("http")) return url;
  const base = getBackendUrl() || "";
  return base + (url.startsWith("/") ? url : "/" + url);
}

function normalizeTicketStatus(raw) {
  if (raw == null || raw === "") return "";
  return String(raw).trim().toLowerCase();
}

function getSignMessagePreference() {
  try {
    const raw = localStorage.getItem("persistentSignMessage");
    if (raw === null) return true;
    return JSON.parse(raw) === true;
  } catch {
    return true;
  }
}

function getMessageTimestamp(msg) {
  const ts = msg?.createdAt ? new Date(msg.createdAt).getTime() : 0;
  return Number.isFinite(ts) ? ts : 0;
}

function compareMessagesAsc(a, b) {
  const byDate = getMessageTimestamp(a) - getMessageTimestamp(b);
  if (byDate !== 0) return byDate;

  const aIdNum = Number(a?.id);
  const bIdNum = Number(b?.id);
  const bothNumeric = Number.isFinite(aIdNum) && Number.isFinite(bIdNum);
  if (bothNumeric && aIdNum !== bIdNum) {
    return aIdNum - bIdNum;
  }

  const aId = String(a?.id ?? "");
  const bId = String(b?.id ?? "");
  return aId.localeCompare(bId);
}

function sortMessagesAsc(list) {
  return [...list].sort(compareMessagesAsc);
}

export default function TicketConversationModal({ open, onClose, ticket }) {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const classes = useStyles();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextPage, setNextPage] = useState(1);
  const [inputMessage, setInputMessage] = useState("");
  const [sending, setSending] = useState(false);
  /** Status atualizado pelo GET /messages (evita card do Kanban desatualizado) */
  const [liveTicketStatus, setLiveTicketStatus] = useState(null);
  const messagesWrapRef = useRef(null);
  const messagesEndRef = useRef(null);
  const didInitialScrollRef = useRef(false);
  const composerInputRef = useRef(null);
  const [quickMessageModalOpen, setQuickMessageModalOpen] = useState(false);

  const effectiveStatus = useMemo(
    () =>
      normalizeTicketStatus(
        liveTicketStatus != null && liveTicketStatus !== ""
          ? liveTicketStatus
          : ticket?.status
      ),
    [liveTicketStatus, ticket?.status]
  );

  const isClosed = effectiveStatus === "closed";
  const isPending = effectiveStatus === "pending";

  const canSendMessage = useMemo(() => {
    if (!ticket?.id || !user) return false;
    if (isClosed) return false;
    if (isPending) return true;
    if (effectiveStatus === "open" || effectiveStatus === "group") return true;
    return false;
  }, [ticket?.id, user, isClosed, isPending, effectiveStatus]);

  useEffect(() => {
    if (!open) {
      setInputMessage("");
      setQuickMessageModalOpen(false);
    }
  }, [open]);

  useEffect(() => {
    setLiveTicketStatus(null);
  }, [ticket?.id, open]);

  const scrollToBottom = () => {
    const wrap = messagesWrapRef.current;
    if (wrap) {
      wrap.scrollTop = wrap.scrollHeight;
      return;
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
    }
  };

  useEffect(() => {
    if (!open || !ticket?.id) {
      setMessages([]);
      setLoadingMore(false);
      setHasMore(false);
      setNextPage(1);
      didInitialScrollRef.current = false;
      return;
    }
    let cancelled = false;
    setLoading(true);
    const ticketId = ticket.id;

    const fetchInitialWindow = async () => {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - INITIAL_DAYS_WINDOW);

      let allDesc = [];
      let pageNumber = 1;
      let localHasMore = true;
      try {
        // Carrega apenas janela recente (ultimos dias), evitando baixar tudo de uma vez.
        while (localHasMore && !cancelled) {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber: String(pageNumber) },
          });
          if (data.ticket?.status != null && !cancelled) {
            setLiveTicketStatus(data.ticket.status);
          }
          const pageMessages = data.messages || [];
          allDesc = allDesc.concat(pageMessages);
          localHasMore = data.hasMore === true;
          pageNumber++;

          if (allDesc.length > 0) {
            const oldestLoaded = allDesc[allDesc.length - 1];
            const oldestDate = oldestLoaded?.createdAt ? new Date(oldestLoaded.createdAt) : null;
            if (oldestDate && oldestDate < cutoff) {
              break;
            }
          } else {
            break;
          }
        }

        const allAsc = sortMessagesAsc(allDesc);
        if (!cancelled) {
          setMessages(allAsc);
          setHasMore(localHasMore);
          setNextPage(pageNumber);
          didInitialScrollRef.current = false;
        }
      } catch (err) {
        if (!cancelled) {
          toastError(err);
          setMessages([]);
          setHasMore(false);
          setNextPage(1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchInitialWindow();
    return () => { cancelled = true; };
  }, [open, ticket?.id]);

  useEffect(() => {
    if (open && !loading && messages.length > 0 && !didInitialScrollRef.current) {
      // Executa após pintura para garantir altura final do container.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottom();
          didInitialScrollRef.current = true;
        });
      });
    }
  }, [open, loading, messages.length]);

  const loadOlderMessages = async () => {
    if (!ticket?.id || loading || loadingMore || !hasMore) return;
    const wrap = messagesWrapRef.current;
    const previousHeight = wrap ? wrap.scrollHeight : 0;
    const previousTop = wrap ? wrap.scrollTop : 0;

    setLoadingMore(true);
    try {
      const { data } = await api.get("/messages/" + ticket.id, {
        params: { pageNumber: String(nextPage) },
      });
      if (data.ticket?.status != null) {
        setLiveTicketStatus(data.ticket.status);
      }
      const pageMessages = data.messages || [];
      const olderAsc = [...pageMessages].reverse();

      setMessages((prev) => sortMessagesAsc([...olderAsc, ...prev]));
      setHasMore(data.hasMore === true);
      setNextPage((prev) => prev + 1);

      // Mantem posição visual ao prepend de mensagens antigas.
      setTimeout(() => {
        const currentWrap = messagesWrapRef.current;
        if (!currentWrap) return;
        const newHeight = currentWrap.scrollHeight;
        currentWrap.scrollTop = newHeight - previousHeight + previousTop;
      }, 0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleMessagesScroll = (e) => {
    if (!didInitialScrollRef.current) return;
    const el = e.target;
    if (el.scrollTop <= 80 && hasMore && !loadingMore && !loading) {
      loadOlderMessages();
    }
  };

  const contactName = ticket?.contact?.name || "";

  const handleOpenTicket = () => {
    onClose();
    if (ticket?.uuid) history.push("/tickets/" + ticket.uuid);
  };

  const mergeMessagesFromPage = useCallback((pageAsc) => {
    setMessages((prev) => {
      const byId = new Map(prev.map((m) => [m.id, m]));
      pageAsc.forEach((m) => {
        if (m.id != null) byId.set(m.id, m);
      });
      return Array.from(byId.values()).sort(compareMessagesAsc);
    });
  }, []);

  const handleQuickMessageMediaSend = useCallback(
    async (selectedMessage) => {
      if (!ticket?.id || !selectedMessage?.mediaPath) return;

      const channel = ticket.channel || ticket.whatsapp?.channel;
      if (channel === "whatsapp_oficial" && !isPending) {
        try {
          const { data } = await api.get(
            `/tickets/${ticket.id}/check-whatsapp-window`
          );
          if (data?.needsTemplate) {
            toast.error(
              "Janela de 24h expirada! Envie um template Meta primeiro para retomar a conversa."
            );
            return;
          }
        } catch {
          /* backend também valida */
        }
      }

      setSending(true);
      try {
        const mediaUrl = selectedMessage.mediaPath;
        // Sempre usar axios: envia Authorization/withCredentials como o resto do app.
        // fetch() na URL absoluta não manda o token — 401/HTML em cache parecem "às vezes não faz nada".
        const { data: blob } = await api.get(mediaUrl, {
          responseType: "blob",
        });

        const originalFileName = selectedMessage.mediaName || null;
        let extension = "bin";
        let filename = null;
        if (originalFileName) {
          const lastDot = originalFileName.lastIndexOf(".");
          if (lastDot !== -1) {
            extension = originalFileName.substring(lastDot + 1).toLowerCase();
            filename = originalFileName;
          } else {
            filename = originalFileName;
          }
        }
        if (!filename) {
          if (blob.type) {
            const mimeToExt = {
              "image/jpeg": "jpg",
              "image/png": "png",
              "image/gif": "gif",
              "image/webp": "webp",
              "video/mp4": "mp4",
              "video/quicktime": "mov",
              "application/pdf": "pdf",
            };
            extension = mimeToExt[blob.type] || blob.type.split("/")[1]?.split(";")[0] || "bin";
            if (extension.length > 10) extension = "bin";
          } else if (selectedMessage.mediaType) {
            const map = { audio: "webm", image: "jpg", video: "mp4", document: "pdf" };
            extension = map[selectedMessage.mediaType] || "bin";
          }
          filename = `${Date.now()}.${extension}`;
        }

        const file = new File([blob], filename, {
          type: blob.type || "",
        });
        const formData = new FormData();
        formData.append("medias", file);
        formData.append("typeArch", "quickMessage");
        const messageBody = (selectedMessage.message || "").trim();
        const body =
          messageBody !== ""
            ? isPending
              ? `\u200d${messageBody}`
              : messageBody
            : isPending
              ? "\u200d"
              : "";
        formData.append("body", body);
        formData.append("fromMe", true);
        formData.append("isPrivate", isPending ? "true" : "false");

        await api.post(`/messages/${ticket.id}`, formData);
        setInputMessage("");
        const { data } = await api.get(`/messages/${ticket.id}`, {
          params: { pageNumber: "1" },
        });
        if (data.ticket?.status != null) {
          setLiveTicketStatus(data.ticket.status);
        }
        const pageAsc = [...(data.messages || [])].reverse();
        mergeMessagesFromPage(pageAsc);
        setTimeout(() => {
          scrollToBottom();
          composerInputRef.current?.focus();
        }, 80);
      } catch (err) {
        const msg = err?.response?.data?.error || err?.message || "";
        if (!String(msg).includes("Janela de 24h")) {
          toastError(err);
        } else {
          toast.error(msg);
        }
      } finally {
        setSending(false);
      }
    },
    [ticket, isPending, mergeMessagesFromPage]
  );

  const handleQuickMessageSelect = useCallback(
    (selectedMessage) => {
      if (!selectedMessage) return;
      if (selectedMessage.mediaPath) {
        void handleQuickMessageMediaSend(selectedMessage);
        return;
      }
      setInputMessage((prev) => {
        const current = prev.trim();
        const text = selectedMessage.message || "";
        return current ? `${current}\n\n${text}` : text;
      });
      setTimeout(() => composerInputRef.current?.focus(), 50);
    },
    [handleQuickMessageMediaSend]
  );

  const handleSendMessage = async () => {
    const trimmed = inputMessage.trim();
    if (!trimmed || !canSendMessage || sending || !ticket?.id || !user) return;

    const channel = ticket.channel || ticket.whatsapp?.channel;
    if (channel === "whatsapp_oficial" && !isPending) {
      try {
        const { data } = await api.get(
          `/tickets/${ticket.id}/check-whatsapp-window`
        );
        if (data?.needsTemplate) {
          toast.error(
            "Janela de 24h expirada! Envie um template Meta primeiro para retomar a conversa."
          );
          return;
        }
      } catch {
        /* backend também valida no POST */
      }
    }

    const userName = isPending
      ? `${user.name} - Mensagem Interna`
      : user.name;
    const signPref = getSignMessagePreference();
    const body =
      (signPref && !isPending) || isPending
        ? `*${userName}:*\n${trimmed}`
        : trimmed;

    const payload = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body,
      quotedMsg: null,
      isPrivate: isPending ? "true" : "false",
    };

    setSending(true);
    try {
      await api.post(`/messages/${ticket.id}`, payload);
      setInputMessage("");
      const { data } = await api.get(`/messages/${ticket.id}`, {
        params: { pageNumber: "1" },
      });
      if (data.ticket?.status != null) {
        setLiveTicketStatus(data.ticket.status);
      }
      const pageDesc = data.messages || [];
      const pageAsc = [...pageDesc].reverse();
      mergeMessagesFromPage(pageAsc);
      setTimeout(() => {
        scrollToBottom();
        composerInputRef.current?.focus();
      }, 80);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "";
      if (!String(msg).includes("Janela de 24h")) {
        toastError(err);
      } else {
        toast.error(msg);
      }
    } finally {
      setSending(false);
    }
  };

  const onComposerKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
    <Dialog
      open={!!(open && ticket)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      disableScrollLock={false}
      PaperProps={{
        style: {
          zIndex: 10010,
        },
      }}
      BackdropProps={{
        style: {
          zIndex: 10009,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
      style={{ zIndex: 10010 }}
      TransitionProps={{
        onEntered: () => {
          if (!didInitialScrollRef.current) {
            scrollToBottom();
            didInitialScrollRef.current = true;
          }
        },
      }}
    >
      <DialogTitle>
        {contactName} – {ticket ? "Conversa" : ""}
      </DialogTitle>
      <DialogContent className={classes.content}>
        {loading ? (
          <div className={classes.loadingWrap}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <div
              ref={messagesWrapRef}
              className={classes.messagesWrap}
              onScroll={handleMessagesScroll}
              style={{ flex: 1, minHeight: 0 }}
            >
              {messages.length === 0 ? (
                <div className={classes.emptyWrap}>
                  <Typography variant="body2">
                    Nenhuma mensagem neste ticket.
                  </Typography>
                </div>
              ) : (
                <>
                  {loadingMore && (
                    <div style={{ display: "flex", justifyContent: "center", padding: 8 }}>
                      <CircularProgress size={18} />
                    </div>
                  )}
                  {messages.map((msg) => {
                    const fromMe = msg.fromMe === true || msg.fromMe === 1 || String(msg.fromMe) === "1";
                    const mediaUrl = getMediaUrl(msg);
                    const isImage = isImageMessage(msg);
                    return (
                      <div
                        key={msg.id}
                        className={`${classes.bubble} ${fromMe ? classes.bubbleFromMe : classes.bubbleFromContact}`}
                      >
                        {isImage && mediaUrl && (
                          <img
                            src={mediaUrl}
                            alt=""
                            className={classes.bubbleMedia}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        )}
                        {msg.body && (
                          <Typography className={classes.bubbleBody} component="div">
                            {msg.body}
                          </Typography>
                        )}
                        {!msg.body && !isImage && mediaUrl && (
                          <Typography className={classes.bubbleBody} component="div">
                            [Mídia]
                          </Typography>
                        )}
                        <div className={classes.bubbleDate}>
                          {msg.createdAt ? format(parseISO(msg.createdAt), "dd/MM/yyyy HH:mm") : ""}
                          {fromMe ? " • Você" : ""}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
            {isClosed && (
              <div className={classes.statusBannerClosed}>
                <InfoOutlinedIcon fontSize="small" />
                <Typography variant="body2" component="div" color="textPrimary">
                  <strong>Ticket encerrado.</strong> Não é possível enviar mensagens por aqui. No Kanban, o
                  envio rápido está disponível apenas para tickets <strong>abertos</strong> em atendimento.
                  Em <strong>aguardando aceite</strong>, apenas mensagens internas são permitidas.
                </Typography>
              </div>
            )}
            {canSendMessage && (
              <div className={classes.composer}>
                <Tooltip title={i18n.t("ticketInfo.quickMessages")}>
                  <span>
                    <IconButton
                      color="primary"
                      edge="start"
                      onClick={() => setQuickMessageModalOpen(true)}
                      disabled={sending}
                      aria-label={i18n.t("ticketInfo.quickMessages")}
                    >
                      <FlashOn />
                    </IconButton>
                  </span>
                </Tooltip>
                <TextField
                  inputRef={composerInputRef}
                  className={classes.composerInput}
                  placeholder={
                    isPending
                      ? "Mensagem interna (não enviada ao cliente)…"
                      : "Digite uma mensagem…"
                  }
                  multiline
                  maxRows={4}
                  variant="outlined"
                  size="small"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={onComposerKeyDown}
                  disabled={sending}
                  fullWidth
                />
                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={sending || !inputMessage.trim()}
                  aria-label="Enviar"
                >
                  {sending ? <CircularProgress size={22} /> : <SendIcon />}
                </IconButton>
              </div>
            )}
            {isPending && !isClosed && (
              <div className={classes.statusBannerPending}>
                <InfoOutlinedIcon fontSize="small" />
                <Typography variant="caption" color="textSecondary" component="div">
                  <strong>Ticket Pendente (aguardando aceite).</strong> Neste momento, apenas
                  <strong> mensagens internas</strong> podem ser enviadas por aqui.
                </Typography>
              </div>
            )}
            {!canSendMessage && ticket && !loading && !isClosed && (
              <div className={classes.composerHint}>
                <Typography variant="caption" color="textSecondary">
                  Este status não permite envio pelo Kanban. Abra o ticket na tela de atendimento.
                </Typography>
              </div>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
        {ticket?.uuid && (
          <Button onClick={handleOpenTicket} color="primary" variant="contained">
            Acessar ticket
          </Button>
        )}
      </DialogActions>
    </Dialog>
    {quickMessageModalOpen && (
      <QuickMessageModal
        open={quickMessageModalOpen}
        onClose={() => setQuickMessageModalOpen(false)}
        onSelect={handleQuickMessageSelect}
        companyId={user?.companyId}
        userId={user?.id}
      />
    )}
    </>
  );
}
